import { useState, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Loader,
  Center,
  Alert,
  Button,
  Text,
  Modal,
  Anchor,
  TextInput,
  Select,
  Divider,
  Group,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  useParams,
  useNavigate,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconAlertTriangle } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import RichTextDiagnosis from "../../components/pathologist-review/RichTextDiagnosis";
import { AddendumEditorModal } from "../../components/pathologist-review/AddendumEditorModal";
import {
  useCase,
  useCaseSpecimens,
  useCaseStatusHistory,
  useCaseFiles,
  useCaseErrorReports,
  useCaseEmails,
  useCaseCommunications,
  useCaseNotes,
  useCaseAdditionalStains,
  useCaseQaReviews,
} from "../../hooks/useCases";
import {
  useCaseLock,
  useAcquireLock,
  useReleaseLock,
  useForceReleaseLock,
  useCompleteTcExternalAction,
  useResolveErrorReport,
  usePrintBlockLabels,
} from "../../hooks/useWorkflows";
import { usePendingCorrectionsForCase } from "../../hooks/useCorrectionRequests";
import { sanitizeHtml } from "../../utils/sanitize";
import { getErrorDisplayName } from "../../utils/formatters";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useSendReport } from "../../hooks/useSendReport";
import { SendReportModal } from "../../components/report-delivery/SendReportModal";
import { useAuth } from "../../hooks/useAuth";
import { useTaskStore } from "../../store/task.store";
import {
  useCaseComments,
  useCaseDiscussions,
} from "../../hooks/useInternalDiscussions";
import { useCaseSendOuts } from "../../hooks/useSendOuts";
import { caseImagesService } from "../../services";
import ErrorReportModal from "../../components/grossing/ErrorReportModal";
import RequestExternalOpinionModal from "../../components/pathologist-review/RequestExternalOpinionModal";
import { ReportPreviewModal } from "../../components/workflows/ReportPreviewModal";
import { SignedReportViewerModal } from "../../components/workflows/SignedReportViewerModal";
import { useActiveSendOuts } from "../../components/send-outs/ActiveSendOutsBanner";
import { casesService, type CaseSpecimen } from "../../services/cases.service";
import { useCassettePrint } from "../../hooks/useCassettePrint";
import {
  slideLabelPrinterService,
  type SlideLabelData,
} from "../../services/slideLabelPrinter.service";
import type { UploadedFile } from "../../types";
import { useFrontOfficeTissueCypherAddendumEligibility } from "../../hooks/useAddendum";
import CaseDetailHeaderV2 from "./components/CaseDetailHeaderV2";
import { shouldShowTechnicalPrintActions } from "./components/caseTechnicalActions";
import {
  ClinicalInfoCardV2,
  PatientClientCardV2,
  InsuranceBillingCardV2,
  CaseMetadataCardV2,
  SpecimensSectionV2,
  ImageGalleryV2,
  CaseDetailTabsV2,
} from "./components";
import { CaseFlagsSection } from "../../components/cases/CaseFlagsSection";
import UpdatePatientInfoModal from "../../components/modals/UpdatePatientInfoModal";
import { downloadReportPdf } from "../../services/signOff.service";
import { downloadFileFromUrl } from "../../utils/downloadFile";
import {
  getDisplayCaseNumber,
  getPhysicalSlideNumber,
} from "../../utils/caseIdentity";
import ReplacePathologistModal from "../../components/cases/ReplacePathologistModal";
import AssignPathologistModal from "../../components/cases/AssignPathologistModal";
import CasePrefixReassignmentModal from "../../components/cases/CasePrefixReassignmentModal";
import { useReportDeliveryRecall } from "../../hooks/useReportDeliveryRecall";
import { RecallReportModal } from "../../components/report-delivery/RecallReportModal";
import { ReportDeliveryHistoryModal } from "../../components/report-delivery/ReportDeliveryHistoryModal";
import RequestStainModal from "../../components/pathologist-review/RequestStainModal";
import {
  calculateAgeFromDateOnly,
  parseDateOnly,
  requireDateOnlyString,
} from "../../utils/dateOnly";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { useCaseAdjacentNavigation } from "../../hooks/useCaseAdjacentNavigation";
import { useTcTechnicalSummaryEligibility } from "../../hooks/useTcTechnicalSummary";
import { tcTechnicalSummaryService } from "../../services/tcTechnicalSummary.service";
import { TcTechnicalSummaryViewerModal } from "../../components/cases/TcTechnicalSummaryViewerModal";
import { parseCaseListNavigationContext } from "../../services/cases.service";
import LegacyImportedCasePanel from "../../components/admin/legacy-cases/LegacyImportedCasePanel";
import { useLegacyImportedCase } from "../../hooks/useLegacyCases";

dayjs.extend(relativeTime);

export function normalizeSpecimensForStainRequest(specimens: CaseSpecimen[]) {
  return specimens.map((specimen) => ({
    ...specimen,
    source: specimen.source ?? undefined,
  }));
}

type PendingSlidePrintAction =
  | { scope: "all" }
  | { scope: "specimen"; specimenId: string };

export default function CaseDetailV2() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedTasks } = useTaskStore();
  const selectedSlugs = selectedTasks.map((t) => t.slug);

  const hasTask = (slug: string) => selectedSlugs.includes(slug);
  const canAccessFrontOffice = hasTask("front-office");
  const canAccessGrossing = hasTask("grossing");
  const canAccessEmbedding = hasTask("embedding");
  const canAccessCutting = hasTask("cutting");
  const canAccessPathologistReview = hasTask("pathologist-review");
  const canUpdateCases = useHasPermission("cases.update");
  const canCompleteCaseCorrections = useHasPermission(
    "case_corrections.complete"
  );
  const canRequestAdditionalStains = useHasPermission(
    "case_details.additional_stains.request"
  );
  const hasTissueCypherAddendumPermission = useHasPermission(
    "case_details.tissue_cypher_addendum.initiate"
  );

  // Tab state — persisted in the URL so the selected tab survives refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "errors";
  const listNavigationFilters = useMemo(
    () => parseCaseListNavigationContext(searchParams),
    [searchParams]
  );
  const adjacentNavigation = useCaseAdjacentNavigation(
    id,
    listNavigationFilters
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(
        (prev) => {
          prev.set("tab", tab);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const tabsRef = useRef<HTMLDivElement>(null);

  const scrollToTab = useCallback(
    (tabValue: string) => {
      setActiveTab(tabValue);
      tabsRef.current?.scrollIntoView({ behavior: "smooth" });
    },
    [setActiveTab]
  );

  const scrollToErrorsTab = useCallback(() => {
    scrollToTab("errors");
  }, [scrollToTab]);

  // Modal state
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [
    externalOpinionModalOpened,
    { open: openExternalOpinionModal, close: closeExternalOpinionModal },
  ] = useDisclosure(false);
  const [duplicateConfirmOpened, { close: closeDuplicateConfirm }] =
    useDisclosure(false);
  const [
    previewModalOpened,
    { open: openPreviewModal, close: closePreviewModal },
  ] = useDisclosure(false);
  const [
    tcTechnicalSummaryOpened,
    { open: openTcTechnicalSummary, close: closeTcTechnicalSummary },
  ] = useDisclosure(false);
  const reportDownloadInFlight = useRef(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const tcSummaryDownloadInFlight = useRef(false);
  const [isDownloadingTcSummary, setIsDownloadingTcSummary] = useState(false);
  const [
    patientInfoModalOpened,
    { open: openPatientInfoModal, close: closePatientInfoModal },
  ] = useDisclosure(false);
  const [resolveModalOpened, setResolveModalOpened] = useState(false);
  const [selectedErrorToResolve, setSelectedErrorToResolve] = useState<
    any | null
  >(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [linkedCorrectionId, setLinkedCorrectionId] = useState<string | null>(
    null
  );

  // Notes state
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  // Delete file state
  const [deleteFileId, setDeleteFileId] = useState<string | null>(null);

  // Upload state
  const [uploadedDocFiles, setUploadedDocFiles] = useState<UploadedFile[]>([]);

  // Slide print loading
  const [slidePrintLoading, setSlidePrintLoading] = useState(false);
  const [pendingSlidePrint, setPendingSlidePrint] =
    useState<PendingSlidePrintAction | null>(null);
  const [
    assignPathologistModalOpened,
    { open: openAssignPathologistModal, close: closeAssignPathologistModal },
  ] = useDisclosure(false);
  const [
    replacePathologistModalOpened,
    { open: openReplacePathologistModal, close: closeReplacePathologistModal },
  ] = useDisclosure(false);
  const [
    prefixReassignmentModalOpened,
    { open: openPrefixReassignmentModal, close: closePrefixReassignmentModal },
  ] = useDisclosure(false);
  const [
    requestStainModalOpened,
    { open: openRequestStainModal, close: closeRequestStainModal },
  ] = useDisclosure(false);
  const [
    tissueCypherAddendumOpened,
    { open: openTissueCypherAddendum, close: closeTissueCypherAddendum },
  ] = useDisclosure(false);
  const [
    tcExternalCompletionOpened,
    { open: openTcExternalCompletion, close: closeTcExternalCompletion },
  ] = useDisclosure(false);

  // ── Data fetching hooks ──────────────────────────────────
  const { data: caseData, isLoading: caseLoading } = useCase(id);
  const legacyReportState = useLegacyImportedCase(
    id || "",
    caseData?.isLegacyImported === true
  );
  const isLegacyTechnicalReport = useMemo(
    () =>
      caseData?.isLegacyImported === true &&
      legacyReportState.data?.reportKind === "technical",
    [caseData?.isLegacyImported, legacyReportState.data?.reportKind]
  );
  const tissueCypherAddendumEligibility =
    useFrontOfficeTissueCypherAddendumEligibility(
      id || "",
      Boolean(id && canAccessFrontOffice && hasTissueCypherAddendumPermission)
    );
  const canInitiateTissueCypherAddendum = useMemo(
    () =>
      canAccessFrontOffice &&
      hasTissueCypherAddendumPermission &&
      tissueCypherAddendumEligibility.data?.canInitiate === true,
    [
      canAccessFrontOffice,
      hasTissueCypherAddendumPermission,
      tissueCypherAddendumEligibility.data?.canInitiate,
    ]
  );
  const displayCaseNumber = useMemo(
    () => getDisplayCaseNumber(caseData),
    [caseData]
  );
  const patientDateOfBirth = useMemo(
    () => parseDateOnly(caseData?.patient?.dateOfBirth),
    [caseData?.patient?.dateOfBirth]
  );
  const labToday = useLabTodayDateOnly();
  const patientAge = useMemo(
    () =>
      patientDateOfBirth
        ? `${calculateAgeFromDateOnly(patientDateOfBirth, labToday)}Y`
        : "",
    [labToday, patientDateOfBirth]
  );
  const hasPathologistReassignmentPermission = useHasPermission(
    "cases.pathologist.reassign"
  );
  const hasPrefixReassignmentPermission = useHasPermission(
    "cases.prefix.reassign"
  );
  const { data: specimens = [] } = useCaseSpecimens(id);
  const stainRequestSpecimens = useMemo(
    () => normalizeSpecimensForStainRequest(specimens),
    [specimens]
  );
  const { data: statusHistory = [] } = useCaseStatusHistory(id);
  const { data: files = [] } = useCaseFiles(id);
  const { data: errors = [] } = useCaseErrorReports(id);
  const blockedCaseMessage = useMemo(
    () =>
      errors.length > 0
        ? "This case has unresolved errors."
        : "This case is blocked for an operational reason unrelated to a reported error.",
    [errors.length]
  );
  useCaseEmails(id);
  const { data: communications = [] } = useCaseCommunications(id);
  const { data: notes = [] } = useCaseNotes(id);
  useCaseAdditionalStains(id);
  const { data: allComments = [] } = useCaseComments(id || "");
  const standaloneComments = allComments.filter(
    (comment: any) => comment.internalDiscussionId === null
  );
  const { data: discussions = [] } = useCaseDiscussions(id || "");
  const { data: sendOuts = [] } = useCaseSendOuts(id);
  const { data: qaReviews = [] } = useCaseQaReviews(id);
  const { activeSendOuts } = useActiveSendOuts(id);

  const handleAdditionalStainSuccess = useCallback(() => {
    if (!id) return;
    queryClient.invalidateQueries({ queryKey: ["case", id] });
    queryClient.invalidateQueries({
      queryKey: ["case-additional-stains", id],
    });
    queryClient.invalidateQueries({ queryKey: ["case-specimens", id] });
  }, [id, queryClient]);

  // ── Send Report ──────────────────────────────────────────
  const {
    handleClick: handleSendReportClick,
    isSending: isSendingReport,
    modalOpened: sendReportModalOpened,
    closeModal: closeSendReportModal,
  } = useSendReport(id);
  const {
    state: reportRecallState,
    stateError: reportRecallStateError,
    remainingSeconds: reportRecallRemainingSeconds,
    countdownPhase: reportRecallCountdownPhase,
    opened: reportRecallModalOpened,
    result: reportRecallResult,
    error: reportRecallError,
    isSubmitting: isReportRecallSubmitting,
    isReconciling: isReportRecallReconciling,
    openModal: openReportRecallModal,
    closeModal: closeReportRecallModal,
    submitRecall: submitReportRecall,
    checkNow: checkReportRecallNow,
  } = useReportDeliveryRecall(
    id,
    Boolean(
      id &&
        caseData?.isFinalized &&
        (!caseData?.isLegacyImported ||
          (legacyReportState.data?.reportKind === "pathology" &&
            legacyReportState.data.latestNativeReportVersion))
    )
  );
  const [
    reportDeliveryHistoryOpened,
    { open: openReportDeliveryHistory, close: closeReportDeliveryHistory },
  ] = useDisclosure(false);

  // ── Lock hooks ───────────────────────────────────────────
  const { data: lockInfo, isLoading: lockLoading } = useCaseLock(id);
  const acquireLockMutation = useAcquireLock();
  const releaseLockMutation = useReleaseLock();
  const forceReleaseLockMutation = useForceReleaseLock();
  const canForceRelease = useHasPermission("case_locks.force_release");
  const completeTcExternalAction = useCompleteTcExternalAction();

  const isLocked = lockInfo && lockInfo.lockedById;
  const isLockedByMe = isLocked && lockInfo.lockedById === user?.id;
  const isLockedByOther = isLocked && lockInfo.lockedById !== user?.id;
  const lockOperationPending =
    acquireLockMutation.isPending ||
    releaseLockMutation.isPending ||
    forceReleaseLockMutation.isPending;

  // ── Lock modals ──────────────────────────────────────────
  const [
    forceUnlockModalOpened,
    { open: openForceUnlockModal, close: closeForceUnlockModal },
  ] = useDisclosure(false);
  const [
    lockReasonModalOpened,
    { open: openLockReasonModal, close: closeLockReasonModal },
  ] = useDisclosure(false);
  const [lockReasonInput, setLockReasonInput] = useState("");

  // ── Derived flags ────────────────────────────────────────
  const currentStatusName = caseData?.currentStatus?.name || "";
  const isSignedOff = caseData?.isFinalized === true;
  const hasSignedReport = isSignedOff || Boolean(caseData?.signedOffAt);
  const fullEditStatuses = ["Draft", "Pending Grossing", "Grossing"];
  const isEditingAllowed =
    !!currentStatusName && fullEditStatuses.includes(currentStatusName);
  const isLimitedEdit = false;
  const isPathologistReviewStatus =
    currentStatusName === "Pending Pathologist Review" ||
    currentStatusName === "Pathologist Review";
  const showGrossingButton =
    currentStatusName === "Pending Grossing" ||
    currentStatusName === "Grossing";
  const showEmbeddingButton =
    currentStatusName === "Pending Tissue Processing" ||
    currentStatusName === "Embedding";
  const showCuttingButton =
    currentStatusName === "Pending Cutting" || currentStatusName === "Cutting";
  const showTechnicalPrintActions = useMemo(
    () => shouldShowTechnicalPrintActions(caseData),
    [caseData]
  );
  const canReplacePathologist = useMemo(
    () =>
      hasPathologistReassignmentPermission && Boolean(caseData?.pathologistId),
    [hasPathologistReassignmentPermission, caseData?.pathologistId]
  );
  const canAssignPathologist = useMemo(
    () =>
      hasPathologistReassignmentPermission &&
      Boolean(caseData) &&
      !caseData?.pathologistId,
    [hasPathologistReassignmentPermission, caseData]
  );
  const showTcExternalCompletion =
    canUpdateCases && caseData?.accessioningType === "tc_only";
  const tcExternalCompletionEnabled =
    showTcExternalCompletion && currentStatusName === "Pending External Report";
  const { data: tcSummaryEligibility } = useTcTechnicalSummaryEligibility(id);
  const showTcTechnicalSummary = tcSummaryEligibility?.eligible === true;

  const handleCompleteTcExternalAction = useCallback(async () => {
    if (!id || !tcExternalCompletionEnabled) return;
    try {
      await completeTcExternalAction.mutateAsync({
        caseId: id,
        expectedAssignmentRevision:
          caseData?.pathologistAssignmentRevision ?? 0,
      });
      closeTcExternalCompletion();
      notifications.show({
        title: "External action completed",
        message: "The TC-only case has moved to Completed.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Unable to complete external action",
        message: "The case could not be completed. Refresh and try again.",
        color: "red",
      });
    }
  }, [
    caseData?.pathologistAssignmentRevision,
    closeTcExternalCompletion,
    completeTcExternalAction,
    id,
    tcExternalCompletionEnabled,
  ]);
  const { data: pendingCorrectionsData } = usePendingCorrectionsForCase(
    id,
    resolveModalOpened
  );
  const correctionOptions = useMemo(
    () =>
      (pendingCorrectionsData?.correctionRequests ?? []).map((cr) => ({
        value: cr.id,
        label: `${cr.requestor.name ?? "Unknown"} · ${
          Object.keys(cr.proposedChanges ?? {}).length
        } field change(s) · ${new Date(cr.requestedAt).toLocaleDateString()}`,
      })),
    [pendingCorrectionsData]
  );

  // ── Mutations ────────────────────────────────────────────
  const resolveErrorMutation = useResolveErrorReport();
  const printLabelsMutation = usePrintBlockLabels();
  const { printLabels } = useCassettePrint();

  // ── Handlers (moved from old CaseDetail.tsx) ─────────────

  const handleDownloadReport = useCallback(async () => {
    if (!id || reportDownloadInFlight.current) return;
    reportDownloadInFlight.current = true;
    setIsDownloadingReport(true);
    try {
      await downloadReportPdf(id, caseData?.caseNumber, { lastSigned: true });
    } catch {
      notifications.show({
        title: "Download failed",
        message: "The signed report could not be downloaded. Please try again.",
        color: "red",
      });
    } finally {
      reportDownloadInFlight.current = false;
      setIsDownloadingReport(false);
    }
  }, [caseData?.caseNumber, id]);

  const handleDownloadTcTechnicalSummary = useCallback(async () => {
    if (!id || tcSummaryDownloadInFlight.current) return;
    tcSummaryDownloadInFlight.current = true;
    setIsDownloadingTcSummary(true);
    try {
      await tcTechnicalSummaryService.download(id);
    } catch {
      notifications.show({
        title: "Download failed",
        message:
          "The Technical Summary could not be downloaded. Please try again.",
        color: "red",
      });
    } finally {
      tcSummaryDownloadInFlight.current = false;
      setIsDownloadingTcSummary(false);
    }
  }, [id]);

  const handleOpenReplacePathologist = useCallback(() => {
    openReplacePathologistModal();
  }, [openReplacePathologistModal]);

  const handleCloseReplacePathologist = useCallback(() => {
    closeReplacePathologistModal();
  }, [closeReplacePathologistModal]);

  const handlePathologistReplaced = useCallback(() => {
    closeReplacePathologistModal();
  }, [closeReplacePathologistModal]);

  const handleOpenAssignPathologist = useCallback(() => {
    openAssignPathologistModal();
  }, [openAssignPathologistModal]);

  const handleCloseAssignPathologist = useCallback(() => {
    closeAssignPathologistModal();
  }, [closeAssignPathologistModal]);

  const handlePathologistAssigned = useCallback(() => {
    closeAssignPathologistModal();
  }, [closeAssignPathologistModal]);

  const handleOpenPrefixReassignment = useCallback(() => {
    openPrefixReassignmentModal();
  }, [openPrefixReassignmentModal]);

  const handleClosePrefixReassignment = useCallback(() => {
    closePrefixReassignmentModal();
  }, [closePrefixReassignmentModal]);

  const handlePrefixReassigned = useCallback(() => {
    closePrefixReassignmentModal();
  }, [closePrefixReassignmentModal]);

  const handleEditCase = useCallback(() => {
    if (isEditingAllowed) {
      navigate(
        isLimitedEdit
          ? `/accessioning/${id}?limitedEdit=1`
          : `/accessioning/${id}`
      );
      return;
    }
    openPatientInfoModal();
  }, [id, isEditingAllowed, isLimitedEdit, navigate, openPatientInfoModal]);
  const handleSignOff = useCallback(() => {
    navigate(`/pathologist-review/${id}`);
  }, [id, navigate]);
  const handleContinueGrossing = useCallback(() => {
    navigate(`/grossing/${id}`);
  }, [id, navigate]);
  const handleContinueEmbedding = useCallback(() => {
    navigate(`/embedding/${id}`);
  }, [id, navigate]);
  const handleContinueCutting = useCallback(() => {
    navigate(`/cutting/${id}`);
  }, [id, navigate]);
  const handleReportError = useCallback(() => {
    setErrorModalOpened(true);
  }, []);

  const handleLockToggle = () => {
    if (!id) return;
    if (isLockedByOther) {
      if (canForceRelease) {
        openForceUnlockModal();
      } else {
        notifications.show({
          title: "Case Locked",
          message: `This case is locked by ${
            lockInfo?.lockedBy?.fullName || "another user"
          } until ${
            lockInfo?.expiresAt
              ? dayjs(lockInfo.expiresAt).format("h:mm A")
              : "unknown"
          }${lockInfo?.lockReason ? ` — ${lockInfo.lockReason}` : ""}`,
          color: "orange",
        });
      }
      return;
    }
    if (isLockedByMe) {
      releaseLockMutation.mutate(id, {
        onSuccess: () => {
          notifications.show({
            title: "Case Unlocked",
            message: "Case is now available for others to edit",
            color: "blue",
          });
        },
        onError: (error: Error) => {
          notifications.show({
            title: "Error",
            message: error.message || "Failed to unlock case",
            color: "red",
          });
        },
      });
    } else {
      setLockReasonInput("");
      openLockReasonModal();
    }
  };

  const handleManualLockConfirm = () => {
    if (!id) return;
    closeLockReasonModal();
    acquireLockMutation.mutate(
      {
        caseId: id,
        workflowStep: "case-details",
        reason: lockReasonInput.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: "Case Locked",
            message: "You have locked this case for editing",
            color: "green",
          });
        },
        onError: (error: Error) => {
          notifications.show({
            title: "Error",
            message: error.message || "Failed to lock case",
            color: "red",
          });
        },
      }
    );
  };

  const handleForceUnlockConfirm = () => {
    if (!id) return;
    closeForceUnlockModal();
    forceReleaseLockMutation.mutate(id, {
      onSuccess: () => {
        notifications.show({
          title: "Case Unlocked",
          message: "Lock has been force-released",
          color: "green",
        });
      },
      onError: (error: Error) => {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to force-unlock case",
          color: "red",
        });
      },
    });
  };

  // Note mutations
  const createNoteMutation = useMutation({
    mutationFn: (noteText: string) => casesService.createNote(id!, noteText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", id] });
      setNewNoteText("");
      setIsAddingNote(false);
      notifications.show({
        title: "Note Added",
        message: "Case note has been saved",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add note",
        color: "red",
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, noteText }: { noteId: string; noteText: string }) =>
      casesService.updateNote(id!, noteId, noteText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", id] });
      setEditingNoteId(null);
      setEditingNoteText("");
      notifications.show({
        title: "Note Updated",
        message: "Case note has been updated",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update note",
        color: "red",
      });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => casesService.deleteNote(id!, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", id] });
      notifications.show({
        title: "Note Deleted",
        message: "Case note has been removed",
        color: "blue",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete note",
        color: "red",
      });
    },
  });

  const handleAddNote = () => {
    if (newNoteText.trim()) {
      createNoteMutation.mutate(newNoteText.trim());
    }
  };

  const handleAddNoteClick = () => {
    scrollToTab("notes");
    setIsAddingNote(true);
  };

  const handleUpdateNote = (noteId: string) => {
    if (editingNoteText.trim()) {
      updateNoteMutation.mutate({ noteId, noteText: editingNoteText.trim() });
    }
  };

  const handleDeleteNote = (noteId: string) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      deleteNoteMutation.mutate(noteId);
    }
  };

  const handleErrorReportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["case-errors", id] });
    queryClient.invalidateQueries({ queryKey: ["case", id] });
  };

  // Print handlers
  const allBlocks = useMemo(
    () => specimens.flatMap((specimen: any) => specimen.blocks || []),
    [specimens]
  );
  const allSlides = useMemo(
    () => allBlocks.flatMap((block: any) => block.slides || []),
    [allBlocks]
  );

  const printAllSlides = useCallback(async () => {
    if (allSlides.length === 0) {
      notifications.show({
        title: "No Slides",
        message: "There are no slides to print",
        color: "orange",
      });
      return;
    }
    setSlidePrintLoading(true);
    try {
      const patientDOB = caseData?.patient?.dateOfBirth
        ? requireDateOnlyString(
            caseData.patient.dateOfBirth,
            "Patient date of birth"
          )
        : undefined;
      const dateReceived = caseData?.dateReceived
        ? requireDateOnlyString(caseData.dateReceived, "Date received")
        : undefined;
      const caseNumber = displayCaseNumber;
      const patientName =
        resolvePatientLabelName(
          caseData?.patient,
          caseData?.draftPatientData
        ) ?? "";

      const labelData: SlideLabelData[] = specimens.flatMap((specimen: any) =>
        (specimen.blocks || []).flatMap((block: any) =>
          (block.slides || []).map((slide: any) => {
            const slideNumber = getPhysicalSlideNumber(slide);
            return {
              caseNumber,
              patientName,
              patientDOB,
              specimenLetter: specimen.number,
              blockNumber: block.number,
              slideNumber,
              stainCode: slide.stain?.code || "H&E",
              barcode: `${caseNumber};${specimen.number};${block.number};${slideNumber}`,
              dateReceived,
              isControlSlide: slide.isControlSlide,
            };
          })
        )
      );

      const result = await slideLabelPrinterService.printSlideLabels(labelData);
      if (result.success) {
        notifications.show({
          title: "Slides Sent to Printer",
          message: `${result.count} slide label(s) sent to ${
            result.method === "bridge" ? "the printer" : "the print folder"
          }.`,
          color: "green",
        });
      } else if (result.method === "browser") {
        notifications.show({
          title: "Folder Required",
          message:
            "Please select a print output folder. Browser printing is not supported for slide labels.",
          color: "orange",
        });
      } else {
        notifications.show({
          title: "Print Error",
          message: result.error || "Failed to print slide labels",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Print Error",
        message: err.message || "Failed to print slide labels",
        color: "red",
      });
    } finally {
      setSlidePrintLoading(false);
    }
  }, [allSlides.length, caseData, displayCaseNumber, specimens]);

  const handlePrintAllCassettes = async () => {
    if (!id || allBlocks.length === 0) {
      notifications.show({
        title: "No Blocks",
        message: "There are no blocks to print",
        color: "orange",
      });
      return;
    }
    try {
      const blockIds = allBlocks.map((b: any) => b.id);
      const result = await printLabelsMutation.mutateAsync({
        caseId: id,
        blockIds,
      });
      const sortedLabels = [...result.labels].sort((a: any, b: any) => {
        const letterCompare = a.specimenLetter.localeCompare(b.specimenLetter);
        if (letterCompare !== 0) return letterCompare;
        return parseInt(a.blockNumber, 10) - parseInt(b.blockNumber, 10);
      });
      await printLabels(sortedLabels);
    } catch (err: any) {
      notifications.show({
        title: "Print Error",
        message: err.message || "Failed to print labels",
        color: "red",
      });
    }
  };

  const handlePrintSpecimenCassettes = async (specimenId: string) => {
    const specimen = specimens.find((s: any) => s.id === specimenId);
    const specimenBlocks = specimen?.blocks || [];
    if (!id || specimenBlocks.length === 0) {
      notifications.show({
        title: "No Blocks",
        message: "This specimen has no blocks to print",
        color: "orange",
      });
      return;
    }
    try {
      const blockIds = specimenBlocks.map((b: any) => b.id);
      const result = await printLabelsMutation.mutateAsync({
        caseId: id,
        blockIds,
      });
      const sortedLabels = [...result.labels].sort((a: any, b: any) => {
        const letterCompare = a.specimenLetter.localeCompare(b.specimenLetter);
        if (letterCompare !== 0) return letterCompare;
        return parseInt(a.blockNumber, 10) - parseInt(b.blockNumber, 10);
      });
      await printLabels(sortedLabels);
    } catch (err: any) {
      notifications.show({
        title: "Print Error",
        message: err.message || "Failed to print labels",
        color: "red",
      });
    }
  };

  const printSpecimenSlides = useCallback(
    async (specimenId: string) => {
      const specimen = specimens.find((s: any) => s.id === specimenId);
      if (!specimen) return;
      const specimenSlides = (specimen.blocks || []).flatMap(
        (block: any) => block.slides || []
      );
      if (specimenSlides.length === 0) {
        notifications.show({
          title: "No Slides",
          message: "This specimen has no slides to print",
          color: "orange",
        });
        return;
      }
      setSlidePrintLoading(true);
      try {
        const patientDOB = caseData?.patient?.dateOfBirth
          ? requireDateOnlyString(
              caseData.patient.dateOfBirth,
              "Patient date of birth"
            )
          : undefined;
        const dateReceived = caseData?.dateReceived
          ? requireDateOnlyString(caseData.dateReceived, "Date received")
          : undefined;
        const caseNumber = displayCaseNumber;
        const patientName =
          resolvePatientLabelName(
            caseData?.patient,
            caseData?.draftPatientData
          ) ?? "";

        const labelData: SlideLabelData[] = (specimen.blocks || []).flatMap(
          (block: any) =>
            (block.slides || []).map((slide: any) => {
              const slideNumber = getPhysicalSlideNumber(slide);
              return {
                caseNumber,
                patientName,
                patientDOB,
                specimenLetter: specimen.number,
                blockNumber: block.number,
                slideNumber,
                stainCode: slide.stain?.code || "H&E",
                barcode: `${caseNumber};${specimen.number};${block.number};${slideNumber}`,
                dateReceived,
                isControlSlide: slide.isControlSlide,
              };
            })
        );

        const result = await slideLabelPrinterService.printSlideLabels(
          labelData
        );
        if (result.success) {
          notifications.show({
            title: "Slides Sent to Printer",
            message: `${result.count} slide label(s) sent to ${
              result.method === "bridge" ? "the printer" : "the print folder"
            }.`,
            color: "green",
          });
        } else if (result.method === "browser") {
          notifications.show({
            title: "Folder Required",
            message:
              "Please select a print output folder. Browser printing is not supported for slide labels.",
            color: "orange",
          });
        } else {
          notifications.show({
            title: "Print Error",
            message: result.error || "Failed to print slide labels",
            color: "red",
          });
        }
      } catch (err: any) {
        notifications.show({
          title: "Print Error",
          message: err.message || "Failed to print slide labels",
          color: "red",
        });
      } finally {
        setSlidePrintLoading(false);
      }
    },
    [caseData, displayCaseNumber, specimens]
  );

  const requestSlidePrint = useCallback(
    async (action: PendingSlidePrintAction) => {
      if (currentStatusName === "Embedding") {
        setPendingSlidePrint(action);
        return;
      }
      if (action.scope === "all") {
        await printAllSlides();
        return;
      }
      await printSpecimenSlides(action.specimenId);
    },
    [currentStatusName, printAllSlides, printSpecimenSlides]
  );

  const handlePrintAllSlides = useCallback(
    () => requestSlidePrint({ scope: "all" }),
    [requestSlidePrint]
  );

  const handlePrintSpecimenSlides = useCallback(
    (specimenId: string) =>
      requestSlidePrint({ scope: "specimen", specimenId }),
    [requestSlidePrint]
  );

  const handleCancelSlidePrint = useCallback(() => {
    setPendingSlidePrint(null);
  }, []);

  const handleConfirmSlidePrint = useCallback(async () => {
    const action = pendingSlidePrint;
    setPendingSlidePrint(null);
    if (!action) return;
    if (action.scope === "all") {
      await printAllSlides();
      return;
    }
    await printSpecimenSlides(action.specimenId);
  }, [pendingSlidePrint, printAllSlides, printSpecimenSlides]);

  // File handlers
  const handleFileDownload = useCallback(
    async (file: { url: string; name: string }) => {
      try {
        const result = await caseImagesService.getDownloadUrl(
          file.url,
          file.name
        );
        try {
          await downloadFileFromUrl(result.url, file.name);
        } finally {
          if (result.url.startsWith("blob:")) URL.revokeObjectURL(result.url);
        }
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to download file",
          color: "red",
        });
      }
    },
    []
  );

  const handleDocFilesChange = useCallback(
    async (newFiles: UploadedFile[]) => {
      setUploadedDocFiles(newFiles);
      const newDocs = newFiles.filter(
        (f): f is UploadedFile & { file: File } => !!f.s3Key && !!f.file
      );
      if (newDocs.length > 0 && id) {
        try {
          await casesService.saveDocuments(
            id,
            newDocs.map((f) => ({
              s3Key: f.s3Key,
              fileName: f.file.name,
              mimeType: f.file.type,
              size: f.file.size,
            })),
            "case_details"
          );
          queryClient.invalidateQueries({ queryKey: ["case-files", id] });
          setUploadedDocFiles([]);
        } catch {
          notifications.show({
            title: "Warning",
            message: "Files uploaded but failed to save to case. Try again.",
            color: "yellow",
          });
        }
      }
    },
    [id, queryClient]
  );

  const handleConfirmDeleteFile = useCallback(async () => {
    if (!id || !deleteFileId) return;
    try {
      await casesService.deleteFile(id, deleteFileId);
      setDeleteFileId(null);
      queryClient.invalidateQueries({ queryKey: ["case-files", id] });
      notifications.show({
        title: "Deleted",
        message: "Document removed",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to delete document",
        color: "red",
      });
    }
  }, [id, deleteFileId, queryClient]);

  // Error resolve handlers
  const handleOpenResolveModal = useCallback((errorReport: any) => {
    setSelectedErrorToResolve(errorReport);
    setResolutionNotes("");
    setLinkedCorrectionId(null);
    setResolveModalOpened(true);
  }, []);

  const handleResolveError = useCallback(() => {
    if (!selectedErrorToResolve || !id) return;
    resolveErrorMutation.mutate(
      {
        errorId: selectedErrorToResolve.id,
        data: {
          resolutionNotes: resolutionNotes.trim(),
          correctionRequestId: linkedCorrectionId,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.refetchQueries({ queryKey: ["case-errors", id] });
          queryClient.invalidateQueries({ queryKey: ["case", id] });
          setResolveModalOpened(false);
          setSelectedErrorToResolve(null);
          setResolutionNotes("");
          setLinkedCorrectionId(null);
          notifications.show({
            title: "Error Resolved",
            message: "The error has been marked as resolved",
            color: "green",
          });
        },
        onError: () => {
          notifications.show({
            title: "Error",
            message: "Failed to resolve error",
            color: "red",
          });
        },
      }
    );
  }, [
    selectedErrorToResolve,
    id,
    resolutionNotes,
    linkedCorrectionId,
    resolveErrorMutation,
    queryClient,
  ]);

  // ── Loading / Error / Guard states ────────────────────────

  if (caseLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!caseData) {
    return (
      <Box p="xl">
        <Alert color="red" title="Case Not Found">
          The case you are looking for does not exist or you do not have
          permission to view it.
        </Alert>
        <Button mt="md" onClick={() => navigate("/cases")}>
          Back to Cases
        </Button>
      </Box>
    );
  }

  // Redirect child case to parent
  if (caseData.parentCaseId) {
    notifications.show({
      title: "Redirecting to Parent Case",
      message:
        "This is an additional stain child case. Opening the parent case instead.",
      color: "blue",
    });
    const contextQuery = searchParams.toString();
    navigate(
      `/cases/${caseData.parentCaseId}${
        contextQuery ? `?${contextQuery}` : ""
      }`,
      { replace: true }
    );
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  const onlyPathologistReview =
    selectedSlugs.length > 0 &&
    selectedSlugs.every((s) => s === "pathologist-review");
  if (
    onlyPathologistReview &&
    currentStatusName === "Draft" &&
    !canUpdateCases
  ) {
    return <Navigate to="/cases" replace />;
  }

  // ── Derived display data ─────────────────────────────────
  const patient = caseData.patient;
  const draftPatient = caseData.draftPatientData as any;
  const patientName = patient
    ? `${patient.firstName} ${patient.lastName}`
    : draftPatient
    ? `${draftPatient.firstName || ""} ${draftPatient.lastName || ""}`.trim()
    : "Unknown Patient";
  const mrn = patient?.medicalRecordNumber || draftPatient?.mrn || "";

  return (
    <Box
      style={{
        background: "var(--mantine-color-gray-0)",
        minHeight: "100vh",
      }}
    >
      <CaseDetailHeaderV2
        caseData={caseData}
        patientName={patientName}
        patientAge={patientAge}
        mrn={mrn}
        currentStatusName={currentStatusName}
        isLocked={!!isLocked}
        isLockedByMe={!!isLockedByMe}
        isLockedByOther={!!isLockedByOther}
        lockInfo={lockInfo}
        lockLoading={lockLoading}
        lockOperationPending={lockOperationPending}
        canAccessFrontOffice={canAccessFrontOffice}
        canAccessGrossing={canAccessGrossing}
        canAccessEmbedding={canAccessEmbedding}
        canAccessCutting={canAccessCutting}
        canAccessPathologistReview={canAccessPathologistReview}
        legacyCanIssueReportRevisions={
          legacyReportState.data?.reportKind === "pathology" &&
          legacyReportState.data.canIssueReportRevisions === true
        }
        legacyHasNativeReport={Boolean(
          legacyReportState.data?.reportKind === "pathology" &&
            legacyReportState.data.latestNativeReportVersion
        )}
        legacyReportKind={legacyReportState.data?.reportKind}
        authProfileId={user?.id}
        isPathologistReviewStatus={isPathologistReviewStatus}
        showGrossingButton={showGrossingButton}
        showEmbeddingButton={showEmbeddingButton}
        showCuttingButton={showCuttingButton}
        showTechnicalPrintActions={showTechnicalPrintActions}
        onPrintAllSlides={handlePrintAllSlides}
        isLockedByOtherDisabled={!!isLockedByOther && !canForceRelease}
        onLockToggle={handleLockToggle}
        onEditCase={handleEditCase}
        onSignOff={handleSignOff}
        onContinueGrossing={handleContinueGrossing}
        onContinueEmbedding={handleContinueEmbedding}
        onContinueCutting={handleContinueCutting}
        onReportError={handleReportError}
        onAddNote={handleAddNoteClick}
        onOpenPreview={openPreviewModal}
        onDownloadReport={handleDownloadReport}
        isDownloadingReport={isDownloadingReport}
        showTcTechnicalSummary={showTcTechnicalSummary}
        onPreviewTcTechnicalSummary={openTcTechnicalSummary}
        onDownloadTcTechnicalSummary={handleDownloadTcTechnicalSummary}
        isDownloadingTcTechnicalSummary={isDownloadingTcSummary}
        onSendReport={handleSendReportClick}
        isSendingReport={isSendingReport}
        scrollToTab={scrollToTab}
        canAssignPathologist={canAssignPathologist}
        onAssignPathologist={handleOpenAssignPathologist}
        canReplacePathologist={canReplacePathologist}
        onReplacePathologist={handleOpenReplacePathologist}
        canReassignPrefix={hasPrefixReassignmentPermission}
        onReassignPrefix={handleOpenPrefixReassignment}
        canRequestAdditionalStains={canRequestAdditionalStains}
        onRequestAdditionalStains={openRequestStainModal}
        canInitiateTissueCypherAddendum={canInitiateTissueCypherAddendum}
        onInitiateTissueCypherAddendum={openTissueCypherAddendum}
        showTcExternalCompletion={showTcExternalCompletion}
        tcExternalCompletionEnabled={tcExternalCompletionEnabled}
        tcExternalCompletionLoading={completeTcExternalAction.isPending}
        onTcExternalCompletion={openTcExternalCompletion}
        reportRecallState={reportRecallState}
        reportRecallRemainingSeconds={reportRecallRemainingSeconds}
        reportRecallCountdownPhase={reportRecallCountdownPhase}
        onRecallReport={openReportRecallModal}
        onOpenReportDeliveryHistory={openReportDeliveryHistory}
        showAdjacentNavigation={adjacentNavigation.enabled}
        previousCaseDisabled={adjacentNavigation.previousDisabled}
        nextCaseDisabled={adjacentNavigation.nextDisabled}
        previousCaseLoading={adjacentNavigation.previousLoading}
        nextCaseLoading={adjacentNavigation.nextLoading}
        onPreviousCase={adjacentNavigation.onPrevious}
        onNextCase={adjacentNavigation.onNext}
      />

      <Modal
        opened={tcExternalCompletionOpened}
        onClose={closeTcExternalCompletion}
        title="Mark external action completed?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This records the TC-only external action and moves the case to
            Completed. This is the same completion used from the pathologist
            work queue.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={closeTcExternalCompletion}
              disabled={completeTcExternalAction.isPending}
            >
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={handleCompleteTcExternalAction}
              loading={completeTcExternalAction.isPending}
            >
              Mark completed
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(pendingSlidePrint)}
        onClose={handleCancelSlidePrint}
        title="Print slides before cutting?"
        centered
      >
        <Stack gap="md">
          <Alert
            color="orange"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Case is still in Embedding"
            role="alert"
          >
            Are you sure you want to print slides? Embedding needs to be
            completed before cutting can start.
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCancelSlidePrint}>
              Cancel
            </Button>
            <Button color="orange" onClick={handleConfirmSlidePrint}>
              Print Slides Anyway
            </Button>
          </Group>
        </Stack>
      </Modal>

      {reportRecallStateError && (
        <Box maw={1600} mx="auto" px="lg" pt="lg">
          <Alert
            color="orange"
            icon={<IconAlertTriangle />}
            title="Report delivery status unavailable"
          >
            <Stack gap="xs" align="flex-start">
              <Text size="sm">{reportRecallStateError.message}</Text>
              <Button variant="light" onClick={checkReportRecallNow}>
                Retry delivery status
              </Button>
            </Stack>
          </Alert>
        </Box>
      )}

      {/* Blocked Alert */}
      {caseData.isBlocked && (
        <Box maw={1600} mx="auto" px="lg" pt="lg">
          <Alert
            color="red"
            icon={<IconAlertTriangle />}
            title="Case Blocked"
            variant="filled"
          >
            <Text fw={500}>{blockedCaseMessage}</Text>
            {errors.length > 0 && (
              <Text size="sm" mt="xs">
                View{" "}
                <Anchor
                  component="button"
                  type="button"
                  onClick={scrollToErrorsTab}
                  underline="always"
                  c="inherit"
                  style={{ cursor: "pointer" }}
                >
                  Errors tab
                </Anchor>{" "}
                for details and resolution.
              </Text>
            )}
          </Alert>
        </Box>
      )}

      {/* Main Content */}
      {caseData.isLegacyImported && id && (
        <Box maw={1600} mx="auto" p="lg">
          <LegacyImportedCasePanel caseId={id} />
        </Box>
      )}
      <Box maw={1600} mx="auto" p="lg">
        <Grid gutter="lg">
          {/* Left Column (70%) */}
          <Grid.Col span={8.4}>
            <Stack gap="lg">
              <ClinicalInfoCardV2 caseData={caseData} />
              <SpecimensSectionV2
                caseId={id!}
                specimens={specimens}
                showTechnicalPrintActions={showTechnicalPrintActions}
                onPrintAllCassettes={handlePrintAllCassettes}
                onPrintAllSlides={handlePrintAllSlides}
                onPrintSpecimenCassettes={handlePrintSpecimenCassettes}
                onPrintSpecimenSlides={handlePrintSpecimenSlides}
                printCassetteLoading={printLabelsMutation.isPending}
                printSlideLoading={slidePrintLoading}
                isFinalized={caseData?.isFinalized}
                isBlocked={caseData?.isBlocked}
                caseNumber={displayCaseNumber}
              />
              <ImageGalleryV2 caseId={id} />
            </Stack>
          </Grid.Col>

          {/* Right Column (30%) */}
          <Grid.Col span={3.6}>
            <Box style={{ position: "sticky", top: 120 }}>
              <Stack gap="lg">
                <PatientClientCardV2 caseData={caseData} />
                <InsuranceBillingCardV2 caseData={caseData} />

                <CaseMetadataCardV2
                  caseData={caseData}
                  canAssignPathologist={canAssignPathologist}
                  onAssignPathologist={handleOpenAssignPathologist}
                  canReplacePathologist={canReplacePathologist}
                  onReplacePathologist={handleOpenReplacePathologist}
                />
                <CaseFlagsSection caseId={id || null} />
              </Stack>
            </Box>
          </Grid.Col>
        </Grid>
      </Box>

      {/* Bottom Tabbed Section */}
      <Box maw={1600} mx="auto" px="lg" pb="xl" ref={tabsRef}>
        <CaseDetailTabsV2
          activeTab={activeTab}
          onTabChange={setActiveTab}
          caseId={id || ""}
          caseData={caseData}
          notes={notes}
          files={files}
          errors={errors}
          statusHistory={statusHistory}
          standaloneComments={standaloneComments}
          discussions={discussions}
          sendOuts={sendOuts}
          communications={communications}
          qaReviews={qaReviews}
          isAddingNote={isAddingNote}
          setIsAddingNote={setIsAddingNote}
          newNoteText={newNoteText}
          setNewNoteText={setNewNoteText}
          editingNoteId={editingNoteId}
          setEditingNoteId={setEditingNoteId}
          editingNoteText={editingNoteText}
          setEditingNoteText={setEditingNoteText}
          onAddNote={handleAddNote}
          onUpdateNote={handleUpdateNote}
          onDeleteNote={handleDeleteNote}
          createNoteLoading={createNoteMutation.isPending}
          updateNoteLoading={updateNoteMutation.isPending}
          onFileDownload={handleFileDownload}
          onFileDelete={(fileId: string) => setDeleteFileId(fileId)}
          uploadedDocFiles={uploadedDocFiles}
          onDocFilesChange={handleDocFilesChange}
          onOpenResolveModal={handleOpenResolveModal}
          onReportError={() => setErrorModalOpened(true)}
        />
      </Box>

      {/* ── Modals ─────────────────────────────────────────── */}

      {id && assignPathologistModalOpened && (
        <AssignPathologistModal
          opened
          onClose={handleCloseAssignPathologist}
          caseId={id}
          expectedAssignmentRevision={
            caseData.pathologistAssignmentRevision ?? 0
          }
          onAssigned={handlePathologistAssigned}
        />
      )}

      {id && replacePathologistModalOpened && (
        <ReplacePathologistModal
          opened
          onClose={handleCloseReplacePathologist}
          caseId={id}
          onReplaced={handlePathologistReplaced}
        />
      )}

      {id && prefixReassignmentModalOpened && (
        <CasePrefixReassignmentModal
          opened
          onClose={handleClosePrefixReassignment}
          caseId={id}
          onReassigned={handlePrefixReassigned}
        />
      )}

      {/* Error Report Modal */}
      {id && (
        <ErrorReportModal
          caseId={id}
          opened={errorModalOpened}
          onClose={() => setErrorModalOpened(false)}
          onSuccess={handleErrorReportSuccess}
        />
      )}

      {/* External Opinion Modal */}
      {id && specimens.length > 0 && (
        <RequestExternalOpinionModal
          caseId={id}
          opened={externalOpinionModalOpened}
          onClose={closeExternalOpinionModal}
          specimens={specimens.map((s: any) => ({
            id: s.id,
            number: s.number,
            source: s.source,
            blocks: s.blocks?.map((b: any) => ({
              id: b.id,
              number: b.number,
              slides: b.slides?.map((sl: any) => ({
                id: sl.id,
                number: sl.number,
                stain: sl.stain,
                isControlSlide: sl.isControlSlide,
              })),
            })),
          }))}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["case", id] });
            queryClient.invalidateQueries({
              queryKey: ["case-specimens", id],
            });
          }}
          onAddInstitution={() => {
            window.open("/admin/external-institutions", "_blank");
          }}
        />
      )}

      {/* Duplicate Send-Out Confirmation */}
      <Modal
        opened={duplicateConfirmOpened}
        onClose={closeDuplicateConfirm}
        title="Active Send-outs Exist"
      >
        <Text size="sm" mb="md">
          There are already active send-out requests for this case. Do you want
          to create another external opinion request?
        </Text>
        <Stack gap="xs" mb="md">
          {activeSendOuts?.map((so: any) => (
            <Text key={so.id} size="xs" c="dimmed">
              • {so.requestType} — {so.status}
            </Text>
          ))}
        </Stack>
        <Grid>
          <Grid.Col span={6}>
            <Button variant="default" fullWidth onClick={closeDuplicateConfirm}>
              Cancel
            </Button>
          </Grid.Col>
          <Grid.Col span={6}>
            <Button
              fullWidth
              onClick={() => {
                closeDuplicateConfirm();
                openExternalOpinionModal();
              }}
            >
              Create Anyway
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>

      {/* Resolve Error Modal */}
      <Modal.Root
        opened={resolveModalOpened}
        onClose={() => {
          setResolveModalOpened(false);
          setSelectedErrorToResolve(null);
        }}
        size="xl"
        centered
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Resolve Error</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            {selectedErrorToResolve && (
              <Stack>
                <Box>
                  <Text size="sm" fw={600}>
                    Error:{" "}
                    {getErrorDisplayName(
                      selectedErrorToResolve.error?.error ||
                        selectedErrorToResolve.discrepancy?.error
                    )}
                  </Text>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--mantine-color-gray-6)",
                      marginTop: 4,
                    }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        selectedErrorToResolve.description || ""
                      ),
                    }}
                  />
                </Box>
                <Box>
                  <Text size="sm" fw={500} mb={4}>
                    Resolution Notes (optional)
                  </Text>
                  <RichTextDiagnosis
                    value={resolutionNotes}
                    onChange={setResolutionNotes}
                    placeholder="Describe how the error was resolved..."
                  />
                </Box>
                <Box>
                  <Text size="sm" fw={500} mb={4}>
                    Link Correction (optional)
                  </Text>
                  <Select
                    placeholder={
                      correctionOptions.length === 0
                        ? "No pending corrections for this case"
                        : "Search corrections for this case..."
                    }
                    searchable
                    clearable
                    data={correctionOptions}
                    value={linkedCorrectionId}
                    onChange={setLinkedCorrectionId}
                    disabled={correctionOptions.length === 0}
                    nothingFoundMessage="No results"
                  />
                </Box>
              </Stack>
            )}
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button
              variant="default"
              onClick={() => setResolveModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleResolveError}
              loading={resolveErrorMutation.isPending}
            >
              Mark as Resolved
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* Delete File Confirmation */}
      <Modal
        opened={!!deleteFileId}
        onClose={() => setDeleteFileId(null)}
        title="Delete File"
      >
        <Text size="sm" mb="md">
          Are you sure you want to delete this file? This action cannot be
          undone.
        </Text>
        <Grid>
          <Grid.Col span={6}>
            <Button
              variant="default"
              fullWidth
              onClick={() => setDeleteFileId(null)}
            >
              Cancel
            </Button>
          </Grid.Col>
          <Grid.Col span={6}>
            <Button fullWidth color="red" onClick={handleConfirmDeleteFile}>
              Delete
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>

      {/* Report Preview Modal (read-only) */}
      {id &&
        !isLegacyTechnicalReport &&
        (hasSignedReport ? (
          <SignedReportViewerModal
            opened={previewModalOpened}
            onClose={closePreviewModal}
            caseId={id}
            caseNumber={displayCaseNumber}
            lastSigned
          />
        ) : (
          <ReportPreviewModal
            opened={previewModalOpened}
            onClose={closePreviewModal}
            caseId={id}
            readOnly
          />
        ))}

      {id && (
        <TcTechnicalSummaryViewerModal
          opened={tcTechnicalSummaryOpened}
          onClose={closeTcTechnicalSummary}
          caseId={id}
          caseNumber={displayCaseNumber}
          originalReport={isLegacyTechnicalReport}
        />
      )}

      {/* Send Report modal */}
      {id && caseData && !isLegacyTechnicalReport && (
        <SendReportModal
          opened={sendReportModalOpened}
          onClose={closeSendReportModal}
          caseId={id}
          physicianId={caseData.physicianId ?? null}
          sendingPracticeId={caseData.sendingPracticeId ?? null}
        />
      )}

      <RecallReportModal
        opened={reportRecallModalOpened}
        onClose={closeReportRecallModal}
        state={reportRecallState}
        result={reportRecallResult}
        error={reportRecallError}
        isSubmitting={isReportRecallSubmitting}
        isReconciling={isReportRecallReconciling}
        onSubmit={submitReportRecall}
        onCheckNow={checkReportRecallNow}
        caseNumber={displayCaseNumber}
      />

      <ReportDeliveryHistoryModal
        opened={reportDeliveryHistoryOpened}
        onClose={closeReportDeliveryHistory}
        state={reportRecallState}
        caseNumber={displayCaseNumber}
      />

      {id && (
        <RequestStainModal
          opened={requestStainModalOpened}
          onClose={closeRequestStainModal}
          caseId={id}
          caseNumber={displayCaseNumber}
          specimens={stainRequestSpecimens}
          onSuccess={handleAdditionalStainSuccess}
          surface="case-details"
        />
      )}

      {id && !caseData.isLegacyImported && (
        <AddendumEditorModal
          opened={tissueCypherAddendumOpened}
          onClose={closeTissueCypherAddendum}
          caseId={id}
          caseNumber={displayCaseNumber}
          draftOnly
          access="front-office-tissue-cypher"
        />
      )}

      {/* Update Patient Info modal */}
      {id && !caseData.isLegacyImported && (
        <UpdatePatientInfoModal
          caseId={id}
          opened={patientInfoModalOpened}
          onClose={closePatientInfoModal}
          isSignedOff={isSignedOff}
          caseData={caseData}
          correctionAmendmentMode={isSignedOff && canCompleteCaseCorrections}
        />
      )}

      {/* Force-unlock confirm modal */}
      <Modal
        opened={forceUnlockModalOpened}
        onClose={closeForceUnlockModal}
        title="Force Unlock Case"
        size="sm"
      >
        <Text size="sm" mb="md">
          This case is locked by{" "}
          <strong>{lockInfo?.lockedBy?.fullName || "another user"}</strong>
          {lockInfo?.lockReason ? ` — ${lockInfo.lockReason}` : ""}. Are you
          sure you want to force-unlock it?
        </Text>
        <Button.Group>
          <Button
            variant="filled"
            color="red"
            loading={forceReleaseLockMutation.isPending}
            onClick={handleForceUnlockConfirm}
          >
            Force Unlock
          </Button>
          <Button variant="default" onClick={closeForceUnlockModal}>
            Cancel
          </Button>
        </Button.Group>
      </Modal>

      {/* Manual lock reason modal */}
      <Modal
        opened={lockReasonModalOpened}
        onClose={closeLockReasonModal}
        title="Lock Case"
        size="sm"
      >
        <TextInput
          label="Reason for locking (optional)"
          placeholder="e.g. Reviewing patient info"
          value={lockReasonInput}
          onChange={(e) => setLockReasonInput(e.currentTarget.value)}
          mb="md"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleManualLockConfirm();
          }}
        />
        <Button.Group>
          <Button
            variant="filled"
            loading={acquireLockMutation.isPending}
            onClick={handleManualLockConfirm}
          >
            Lock Case
          </Button>
          <Button variant="default" onClick={closeLockReasonModal}>
            Cancel
          </Button>
        </Button.Group>
      </Modal>
    </Box>
  );
}
